import type {
  Analytics,
  AdminSmtpSettings,
  AdminSmtpUpdate,
  AlertSettings,
  AppearanceSettings,
  Availability,
  AvailabilitySegment,
  BackupRecord,
  BackupSettings,
  BackupSettingsUpdate,
  Anomaly,
  Device,
  DeviceUpdateRequest,
  DiskMetric,
  EnrollmentCredentials,
  EnrollmentRequest,
  Forecast,
  MetricSeries,
  MetricSnapshot,
  NetworkMetric,
  PeakWindow,
  PublicConfig,
  SeriesPoint,
  SmtpTestResult,
  UptimeEntry,
  UptimeSummary,
} from '@/types/monitoring'
import type { AdminSession, OidcSettings } from '@/types/oidc'
import { aggregateDisks } from '@/utils/storage'
import { i18n } from '@/i18n'
import { normalizeAppearance } from '@/utils/appearance'

const API_BASE = (import.meta.env.VITE_API_BASE || '/api/v1').replace(/\/$/, '')

let csrfToken = ''
const uptimeCache = new Map<string, { expires: number; data: UptimeSummary }>()
const uptimeRequests = new Map<string, Promise<UptimeSummary>>()
let queryCacheGeneration = 0

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type UnknownRecord = Record<string, unknown>

function record(value: unknown): UnknownRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : {}
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function number(value: unknown, fallback = 0): number {
  const parsed = typeof value === 'string' ? Number(value) : value
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : fallback
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function boolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function stringRecord(value: unknown): Record<string, string> {
  return Object.fromEntries(
    Object.entries(record(value)).filter((entry): entry is [string, string] => {
      return typeof entry[1] === 'string'
    }),
  )
}

function pick(source: UnknownRecord, ...keys: string[]): unknown {
  for (const key of keys) if (source[key] !== undefined && source[key] !== null) return source[key]
  return undefined
}

async function request<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    signal: init.signal
      ? AbortSignal.any([init.signal, AbortSignal.timeout(30_000)])
      : AbortSignal.timeout(30_000),
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })
  if (!response.ok) {
    let message = i18n.global.t('errors.requestFailed', { status: response.status })
    try {
      const payload = record(await response.json())
      if (typeof payload.detail === 'string' && payload.detail.trim()) message = payload.detail
    } catch {
      // The HTTP status remains useful when an upstream returns a non-JSON error page.
    }
    throw new ApiError(message, response.status)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

function normalizeDisk(value: unknown): DiskMetric {
  const raw = record(value)
  return {
    device: text(raw.device, 'disk'),
    mountpoint: text(raw.mountpoint, '/'),
    filesystem: text(pick(raw, 'filesystem', 'fs_type')),
    totalBytes: number(pick(raw, 'total_bytes', 'totalBytes')),
    usedBytes: number(pick(raw, 'used_bytes', 'usedBytes')),
    usedPercent: number(pick(raw, 'used_percent', 'usedPercent')),
    readBytesPerSecond: number(pick(raw, 'read_bytes_per_sec', 'read_bytes_per_second')),
    writeBytesPerSecond: number(pick(raw, 'write_bytes_per_sec', 'write_bytes_per_second')),
    readIops: number(pick(raw, 'read_iops', 'readIops')),
    writeIops: number(pick(raw, 'write_iops', 'writeIops')),
  }
}

function normalizeNetwork(value: unknown): NetworkMetric {
  const raw = record(value)
  return {
    interface: text(raw.interface, 'interface'),
    rxBytesPerSecond: number(pick(raw, 'rx_bytes_per_sec', 'rx_bytes_per_second')),
    txBytesPerSecond: number(pick(raw, 'tx_bytes_per_sec', 'tx_bytes_per_second')),
    rxBytesTotal: number(raw.rx_bytes_total),
    txBytesTotal: number(raw.tx_bytes_total),
  }
}

function normalizeAlerts(value: unknown): AlertSettings {
  const raw = record(value)
  const strategy = (value: unknown, fallback: 'state_change' | 'continuous') =>
    value === 'state_change' || value === 'continuous' ? value : fallback
  return {
    cpuPercent: number(raw.cpu_percent, 90),
    memoryPercent: number(raw.memory_percent, 90),
    diskPercent: number(raw.disk_percent, 90),
    networkBytesPerSecond: number(raw.network_bytes_per_sec, 125_000_000),
    offlineSeconds: number(raw.offline_seconds, 30),
    debounceSeconds: number(raw.debounce_seconds, 30),
    cooldownSeconds: number(raw.cooldown_seconds, 900),
    emailRecipients: array(raw.email_recipients).filter(
      (item): item is string => typeof item === 'string',
    ),
    cpuEnabled: boolean(raw.cpu_enabled, true),
    memoryEnabled: boolean(raw.memory_enabled, true),
    diskEnabled: boolean(raw.disk_enabled, true),
    networkEnabled: boolean(raw.network_enabled, true),
    offlineEnabled: boolean(raw.offline_enabled, true),
    cpuDurationSeconds: number(raw.cpu_duration_seconds, 30),
    memoryDurationSeconds: number(raw.memory_duration_seconds, 30),
    diskDurationSeconds: number(raw.disk_duration_seconds, 30),
    networkDurationSeconds: number(raw.network_duration_seconds, 30),
    offlineDurationSeconds: number(raw.offline_duration_seconds),
    cpuStrategy: strategy(raw.cpu_strategy, 'continuous'),
    memoryStrategy: strategy(raw.memory_strategy, 'continuous'),
    diskStrategy: strategy(raw.disk_strategy, 'continuous'),
    networkStrategy: strategy(raw.network_strategy, 'continuous'),
    offlineStrategy: strategy(raw.offline_strategy, 'state_change'),
  }
}

function normalizeAdminSmtp(value: unknown): AdminSmtpSettings {
  const raw = record(value)
  const security =
    raw.security === 'ssl' || raw.security === 'none'
      ? raw.security
      : number(raw.port, 587) === 465
        ? 'ssl'
        : boolean(raw.starttls, true)
          ? 'starttls'
          : 'none'
  return {
    enabled: boolean(raw.enabled),
    host: text(raw.host),
    port: number(raw.port, 587),
    username: text(raw.username),
    passwordConfigured: boolean(raw.password_configured),
    senderAddress: text(raw.sender_address),
    security,
    alertTemplateHtml: text(raw.alert_template_html, text(raw.template_html)),
    recoveryTemplateHtml: text(
      raw.recovery_template_html,
      text(raw.alert_template_html, text(raw.template_html)),
    ),
  }
}

function smtpRequest(settings: AdminSmtpUpdate): Record<string, unknown> {
  return {
    enabled: settings.enabled,
    host: settings.host,
    port: settings.port,
    username: settings.username,
    password: settings.password || null,
    clear_password: settings.clearPassword,
    sender_address: settings.senderAddress,
    security: settings.security,
    starttls: settings.security === 'starttls',
    template_html: settings.alertTemplateHtml,
    alert_template_html: settings.alertTemplateHtml,
    recovery_template_html: settings.recoveryTemplateHtml,
  }
}

function normalizeBackupSettings(value: unknown): BackupSettings {
  const raw = record(value)
  return {
    scheduleEnabled: boolean(raw.schedule_enabled),
    intervalDays: number(raw.interval_days, 7),
    target: raw.target === 'remote' ? 'remote' : 'local',
    endpointUrl: text(raw.endpoint_url),
    region: text(raw.region, 'us-east-1'),
    bucketName: text(raw.bucket_name),
    accessKey: text(raw.access_key),
    secretKeyConfigured: boolean(raw.secret_key_configured),
  }
}

function normalizeBackupRecord(value: unknown): BackupRecord {
  const raw = record(value)
  const status = text(raw.status)
  return {
    id: text(raw.id),
    filename: text(raw.filename),
    target: raw.target === 'remote' ? 'remote' : 'local',
    trigger: raw.trigger === 'scheduled' ? 'scheduled' : 'manual',
    status:
      status === 'running' || status === 'completed' || status === 'failed' ? status : 'pending',
    sizeBytes: optionalNumber(raw.size_bytes),
    sha256: text(raw.sha256) || null,
    createdAt: text(raw.created_at),
    completedAt: text(raw.completed_at) || null,
    error: text(raw.error) || null,
    downloadable: boolean(raw.downloadable),
  }
}

export function normalizeMetrics(value: unknown): MetricSnapshot | null {
  const raw = record(value)
  if (Object.keys(raw).length === 0) return null
  const cpu = record(raw.cpu)
  const memory = record(raw.memory)
  const load = record(raw.load)
  return {
    cpu: {
      userPercent: number(pick(cpu, 'user_percent', 'userPercent')),
      systemPercent: number(pick(cpu, 'system_percent', 'systemPercent')),
      totalPercent: number(pick(cpu, 'total_percent', 'totalPercent')),
    },
    memory: {
      totalBytes: number(pick(memory, 'total_bytes', 'totalBytes')),
      usedBytes: number(pick(memory, 'used_bytes', 'usedBytes')),
      usedPercent: number(pick(memory, 'used_percent', 'usedPercent')),
      swapTotalBytes: number(pick(memory, 'swap_total_bytes', 'swapTotalBytes')),
      swapUsedBytes: number(pick(memory, 'swap_used_bytes', 'swapUsedBytes')),
      swapUsedPercent: number(pick(memory, 'swap_used_percent', 'swapUsedPercent')),
    },
    load: {
      one: number(load.one),
      five: number(load.five),
      fifteen: number(load.fifteen),
    },
    disks: aggregateDisks(array(raw.disks).map(normalizeDisk)),
    networks: array(raw.networks).map(normalizeNetwork),
    controllerLatencyMs: optionalNumber(
      pick(raw, 'controller_latency_ms', 'controllerLatencyMs', 'latency_ms'),
    ),
  }
}

export function normalizeDevice(value: unknown): Device {
  const raw = record(value)
  const latest = record(pick(raw, 'latest', 'current', 'snapshot'))
  const status = text(raw.status).toLowerCase()
  return {
    id: text(pick(raw, 'id', 'device_id', 'agent_id')),
    name: text(pick(raw, 'name', 'display_name', 'hostname'), i18n.global.t('dashboard.noNodes')),
    displayId: optionalNumber(raw.display_id),
    sourceIp: text(raw.source_ip) || null,
    countryCode: text(raw.country_code) || null,
    countryOverride: text(raw.country_override) || null,
    description: text(raw.description) || undefined,
    labels: stringRecord(raw.labels),
    online: boolean(raw.online, status === 'online'),
    enabled: boolean(raw.enabled, status !== 'disabled'),
    lastSeen: text(pick(raw, 'last_seen', 'lastSeen', 'received_at')) || null,
    uptimeSeconds: number(
      pick(
        raw,
        'uptime_seconds',
        'uptimeSeconds',
        'server_uptime_seconds',
        'server_session_uptime_seconds',
      ),
    ),
    expectedIntervalSeconds: number(
      pick(raw, 'expected_interval_seconds', 'heartbeat_interval_seconds'),
      5,
    ),
    metrics: normalizeMetrics(raw.metrics ?? raw.current_metrics ?? latest.metrics ?? latest),
  }
}

export function normalizeEnrollment(value: unknown): EnrollmentCredentials {
  const raw = record(value)
  return {
    agentId: text(pick(raw, 'agent_id', 'agentId')),
    name: text(raw.name),
    token: text(raw.token),
    expectedIntervalSeconds: number(
      pick(raw, 'expected_interval_seconds', 'expectedIntervalSeconds'),
      5,
    ),
    createdAt: text(pick(raw, 'created_at', 'createdAt')),
    warning: i18n.global.t('enrollment.tokenWarning'),
  }
}

function timestamp(value: unknown): number {
  if (typeof value === 'number') return value < 10_000_000_000 ? value * 1000 : value
  const parsed = Date.parse(text(value))
  return Number.isFinite(parsed) ? parsed : 0
}

function point(value: unknown): SeriesPoint | null {
  if (Array.isArray(value)) {
    const timeValue = value[0]
    const metricValue = value[1]
    const time = timestamp(timeValue)
    return time ? { timestamp: time, value: number(metricValue) } : null
  }
  const raw = record(value)
  const time = timestamp(pick(raw, 'timestamp', 'time', 'received_at', 'ts'))
  return time ? { timestamp: time, value: number(pick(raw, 'value', 'avg', 'metric')) } : null
}

function normalizePoints(value: unknown): SeriesPoint[] {
  return array(value)
    .map(point)
    .filter((item): item is SeriesPoint => item !== null)
}

function emptySeries(): MetricSeries {
  return {
    cpuTotal: [],
    cpuUser: [],
    cpuSystem: [],
    memoryUsed: [],
    swapUsed: [],
    networkRx: [],
    networkTx: [],
    diskUsed: [],
    diskRead: [],
    diskWrite: [],
    loadOne: [],
    loadFive: [],
    loadFifteen: [],
    controllerLatency: [],
    networkTrafficBytes: null,
    networkP95BytesPerSecond: null,
    networkP99BytesPerSecond: null,
    diskP95BytesPerSecond: null,
    diskP99BytesPerSecond: null,
  }
}

function appendSnapshot(series: MetricSeries, value: unknown): void {
  const raw = record(value)
  const time = timestamp(pick(raw, 'received_at', 'received_at_ms', 'timestamp', 'time', 'ts'))
  const metrics = normalizeMetrics(raw.metrics ?? raw.payload ?? raw)
  if (!time || !metrics) return
  const add = (target: SeriesPoint[], metricValue: number) =>
    target.push({ timestamp: time, value: metricValue })
  add(series.cpuTotal, metrics.cpu.totalPercent)
  add(series.cpuUser, metrics.cpu.userPercent)
  add(series.cpuSystem, metrics.cpu.systemPercent)
  add(series.memoryUsed, metrics.memory.usedPercent)
  add(series.swapUsed, metrics.memory.swapUsedPercent)
  add(
    series.networkRx,
    metrics.networks.reduce((sum, item) => sum + item.rxBytesPerSecond, 0),
  )
  add(
    series.networkTx,
    metrics.networks.reduce((sum, item) => sum + item.txBytesPerSecond, 0),
  )
  add(series.diskUsed, Math.max(0, ...metrics.disks.map((item) => item.usedPercent)))
  add(
    series.diskRead,
    metrics.disks.reduce((sum, item) => sum + item.readBytesPerSecond, 0),
  )
  add(
    series.diskWrite,
    metrics.disks.reduce((sum, item) => sum + item.writeBytesPerSecond, 0),
  )
  add(series.loadOne, metrics.load.one)
  add(series.loadFive, metrics.load.five)
  add(series.loadFifteen, metrics.load.fifteen)
  if (metrics.controllerLatencyMs !== null) {
    add(series.controllerLatency, metrics.controllerLatencyMs)
  }
}

export function normalizeSeries(value: unknown): MetricSeries {
  const raw = record(value)
  const snapshots = array(pick(raw, 'points', 'items', 'samples', 'metrics'))
  const result = emptySeries()
  const summary = record(raw.summary)
  result.summaryApproximate = boolean(summary.approximate)
  result.networkTrafficBytes = optionalNumber(summary.network_rx_bytes)
  if (result.networkTrafficBytes !== null) {
    result.networkTrafficBytes += number(summary.network_tx_bytes)
  }
  result.networkP95BytesPerSecond = optionalNumber(summary.network_p95_bytes_per_sec)
  result.networkP99BytesPerSecond = optionalNumber(summary.network_p99_bytes_per_sec)
  result.diskP95BytesPerSecond = optionalNumber(summary.disk_p95_bytes_per_sec)
  result.diskP99BytesPerSecond = optionalNumber(summary.disk_p99_bytes_per_sec)
  if (snapshots.length) {
    snapshots.forEach((item) => appendSnapshot(result, item))
    return result
  }
  const source = record(raw.series ?? raw)
  result.cpuTotal = normalizePoints(pick(source, 'cpu_total', 'cpuTotal', 'cpu.total_percent'))
  result.cpuUser = normalizePoints(pick(source, 'cpu_user', 'cpuUser', 'cpu.user_percent'))
  result.cpuSystem = normalizePoints(pick(source, 'cpu_system', 'cpuSystem', 'cpu.system_percent'))
  result.memoryUsed = normalizePoints(
    pick(source, 'memory_used', 'memoryUsed', 'memory.used_percent'),
  )
  result.swapUsed = normalizePoints(
    pick(source, 'swap_used', 'swapUsed', 'memory.swap_used_percent'),
  )
  result.networkRx = normalizePoints(pick(source, 'network_rx', 'networkRx', 'network.rx'))
  result.networkTx = normalizePoints(pick(source, 'network_tx', 'networkTx', 'network.tx'))
  result.diskUsed = normalizePoints(pick(source, 'disk_used', 'diskUsed', 'disk.used_percent'))
  result.diskRead = normalizePoints(pick(source, 'disk_read', 'diskRead', 'disk.read'))
  result.diskWrite = normalizePoints(pick(source, 'disk_write', 'diskWrite', 'disk.write'))
  result.loadOne = normalizePoints(pick(source, 'load_one', 'loadOne', 'load.one'))
  result.loadFive = normalizePoints(pick(source, 'load_five', 'loadFive', 'load.five'))
  result.loadFifteen = normalizePoints(pick(source, 'load_fifteen', 'loadFifteen', 'load.fifteen'))
  result.controllerLatency = normalizePoints(
    pick(source, 'controller_latency', 'controllerLatency', 'controller_latency_ms'),
  )
  return result
}

function normalizeUptimeEntry(value: unknown): UptimeEntry {
  const raw = record(value)
  const validStatuses = new Set(['online', 'offline', 'degraded', 'unknown'])
  const segments: AvailabilitySegment[] = array(raw.segments)
    .map((seg) => {
      const s = record(seg)
      const status = text(s.status)
      return {
        start: text(s.start),
        end: text(s.end),
        status: validStatuses.has(status) ? (status as AvailabilitySegment['status']) : 'unknown',
        uptimePercent: optionalNumber(s.uptime_percent),
        unknownSeconds: number(s.unknown_seconds),
        onlineSeconds: number(s.online_seconds),
        offlineSeconds: number(s.offline_seconds),
      }
    })
    .filter((seg) => seg.start && seg.end)
  return {
    id: text(raw.id),
    displayId: optionalNumber(raw.display_id),
    countryCode: text(raw.country_code) || null,
    name: text(raw.name),
    online: boolean(raw.online),
    status: text(raw.status, 'unknown'),
    uptimePercent: optionalNumber(raw.uptime_percent),
    unknownSeconds: number(raw.unknown_seconds),
    coveragePercent: number(raw.coverage_percent, 100),
    onlineSeconds: number(raw.online_seconds),
    offlineSeconds: number(raw.offline_seconds),
    monitoredFrom: text(raw.monitored_from) || null,
    segments,
  }
}

export function normalizeAvailability(value: unknown): Availability {
  const raw = record(value)
  const validStatuses = new Set(['online', 'offline', 'degraded', 'unknown'])
  const segments: AvailabilitySegment[] = array(raw.segments)
    .map((value) => {
      const segment = record(value)
      const status = text(segment.status)
      return {
        start: text(segment.start),
        end: text(segment.end),
        status: validStatuses.has(status) ? (status as AvailabilitySegment['status']) : 'unknown',
        uptimePercent: optionalNumber(segment.uptime_percent),
        unknownSeconds: number(segment.unknown_seconds),
        onlineSeconds: number(segment.online_seconds),
        offlineSeconds: number(segment.offline_seconds),
      }
    })
    .filter((segment) => segment.start && segment.end)
  return {
    from: text(raw.from),
    to: text(raw.to),
    monitoredFrom: text(raw.monitored_from) || null,
    offlineAfterSeconds: number(raw.offline_after_seconds),
    unknownSeconds: number(raw.unknown_seconds),
    coveragePercent: number(raw.coverage_percent, 100),
    onlineSeconds: number(raw.online_seconds),
    offlineSeconds: number(raw.offline_seconds),
    uptimePercent: optionalNumber(raw.uptime_percent),
    segments,
  }
}

function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null
  const parsed = typeof value === 'string' ? Number(value) : value
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null
}

function normalizeForecasts(value: unknown): Forecast[] {
  const direct = array(value)
  const grouped = record(value)
  const values = direct.length
    ? direct
    : grouped.resource
      ? [grouped]
      : [grouped.memory, ...array(grouped.disks)].filter(Boolean)

  return values.map((value) => {
    const item = record(value)
    const resource = text(item.resource, 'resource')
    const target = text(item.target)
    return {
      resource,
      target,
      direction: text(pick(item, 'direction', 'status'), 'stable').replace(/_/g, ' '),
      slopePerDay: optionalNumber(pick(item, 'slope_per_day', 'slopePerDay')),
      growthBytesPerDay: optionalNumber(pick(item, 'growth_bytes_per_day', 'growthBytesPerDay')),
      estimatedExhaustionAt: text(pick(item, 'estimated_exhaustion_at', 'exhaustion_at')) || null,
      confidence: optionalNumber(pick(item, 'confidence', 'r_squared')),
    }
  })
}

function peakWindows(value: unknown, behavior: UnknownRecord): PeakWindow[] {
  const direct = array(value).map((value) => {
    const item = record(value)
    return {
      hour: number(pick(item, 'hour', 'hour_utc')),
      average: number(pick(item, 'average', 'value', 'mean')),
      metric: text(item.metric, 'utilization').replace(/_/g, ' '),
    }
  })
  if (direct.length) return direct

  const bestByMetric = new Map<string, PeakWindow>()
  for (const value of array(behavior.profile)) {
    const bucket = record(value)
    const hour = number(pick(bucket, 'hour', 'hour_utc'))
    for (const [metric, metricValue] of Object.entries(record(bucket.metrics))) {
      const average = optionalNumber(pick(record(metricValue), 'average', 'mean', 'value'))
      const existing = bestByMetric.get(metric)
      if (average !== null && (!existing || average > existing.average)) {
        bestByMetric.set(metric, {
          hour,
          average,
          metric: metric.replace(/_/g, ' '),
        })
      }
    }
  }
  return [...bestByMetric.values()].sort((left, right) => right.average - left.average)
}

export function normalizeAnalytics(value: unknown): Analytics {
  const raw = record(value)
  const bandwidth = record(pick(raw, 'bandwidth', 'percentiles'))
  const aggregate = record(bandwidth.aggregate)
  const bandwidthTotal = record(
    pick(aggregate, 'total_bytes_per_sec', 'total_bytes_per_second', 'totalBytesPerSecond'),
  )
  const behavior = record(raw.behavior)
  const forecasts = normalizeForecasts(pick(raw, 'forecasts', 'forecast'))
  const peaks = peakWindows(pick(raw, 'peak_windows', 'peaks'), behavior)
  const anomalies: Anomaly[] = array(pick(raw, 'anomalies') ?? behavior.anomalies).map((value) => {
    const item = record(value)
    return {
      timestamp: text(pick(item, 'timestamp', 'time', 'received_at')),
      metric: text(item.metric, 'utilization').replace(/_/g, ' '),
      value: number(item.value),
      score: number(pick(item, 'score', 'z_score')),
    }
  })
  const p95 = pick(bandwidth, 'p95_bytes_per_second', 'p95', 'percentile_95')
  const p99 = pick(bandwidth, 'p99_bytes_per_second', 'p99', 'percentile_99')
  return {
    bandwidth: {
      p95BytesPerSecond: optionalNumber(p95 ?? bandwidthTotal.p95),
      p99BytesPerSecond: optionalNumber(p99 ?? bandwidthTotal.p99),
    },
    forecasts,
    peakWindows: peaks,
    anomalies,
    enabled: boolean(raw.enabled, true),
  }
}

export const api = {
  clearQueryCache(): void {
    queryCacheGeneration += 1
    uptimeCache.clear()
    uptimeRequests.clear()
  },
  cachedUptime(token: string, days = 7): UptimeSummary | null {
    const cached = uptimeCache.get(`${token}:${days}`)
    return cached && cached.expires > Date.now() ? cached.data : null
  },
  setCsrfToken(value: string): void {
    csrfToken = value
  },
  async session(): Promise<AdminSession> {
    return request<AdminSession>('/auth/session', '')
  },
  async loginConfig(): Promise<{ oidc_enabled: boolean; login_url: string }> {
    return request('/auth/config', '')
  },
  async logout(provider = false): Promise<{ redirect_url: string; provider_logout: boolean }> {
    return request(`/auth/logout?provider=${provider}`, '', { method: 'POST' })
  },
  async logoutStatus(state: string): Promise<{ logged_out: boolean; provider_completed: boolean }> {
    return request(`/auth/logout/status?state=${encodeURIComponent(state)}`, '')
  },
  async oidcSettings(token: string): Promise<OidcSettings> {
    return request('/admin/oidc', token)
  },
  async updateOidcSettings(token: string, value: OidcSettings): Promise<OidcSettings> {
    const body = {
      enabled: value.enabled,
      issuer: value.issuer,
      client_id: value.client_id,
      client_secret: value.client_secret,
      clear_client_secret: value.clear_client_secret,
      scopes: value.scopes,
      token_endpoint_auth_method: value.token_endpoint_auth_method,
      allowed_subjects: value.allowed_subjects,
      allowed_emails: value.allowed_emails,
    }
    return request('/admin/oidc', token, { method: 'PUT', body: JSON.stringify(body) })
  },
  async testOidc(token: string): Promise<void> {
    await request('/admin/oidc/test', token, { method: 'POST' })
  },
  async validateToken(token: string): Promise<void> {
    await request<unknown>('/devices?limit=1', token)
  },

  async devices(token: string): Promise<Device[]> {
    const payload = await request<unknown>('/devices', token)
    const raw = record(payload)
    const items = Array.isArray(payload) ? payload : array(pick(raw, 'items', 'devices', 'data'))
    return items.map(normalizeDevice).filter((device) => device.id)
  },

  async enrollDevice(token: string, enrollment: EnrollmentRequest): Promise<EnrollmentCredentials> {
    return normalizeEnrollment(
      await request<unknown>('/devices', token, {
        method: 'POST',
        body: JSON.stringify({
          name: enrollment.name,
          expected_interval_seconds: enrollment.expectedIntervalSeconds,
          labels: enrollment.labels ?? {},
        }),
      }),
    )
  },

  async device(token: string, id: string, signal?: AbortSignal): Promise<Device> {
    return normalizeDevice(
      await request<unknown>(`/devices/${encodeURIComponent(id)}`, token, { signal }),
    )
  },

  async updateDevice(token: string, id: string, update: DeviceUpdateRequest): Promise<Device> {
    return normalizeDevice(
      await request<unknown>(`/devices/${encodeURIComponent(id)}`, token, {
        method: 'PATCH',
        body: JSON.stringify({
          ...(update.displayId !== undefined ? { display_id: update.displayId } : {}),
          ...(update.countryCode !== undefined ? { country_code: update.countryCode } : {}),
          name: update.name,
          labels: update.labels,
          expected_interval_seconds: update.expectedIntervalSeconds,
        }),
      }),
    )
  },

  async deleteDevice(token: string, id: string): Promise<void> {
    await request<void>(`/devices/${encodeURIComponent(id)}`, token, { method: 'DELETE' })
  },

  async metrics(
    token: string,
    id: string,
    from: Date,
    to: Date,
    maxPoints = 600,
    signal?: AbortSignal,
  ): Promise<MetricSeries> {
    const query = new URLSearchParams({
      from: from.toISOString(),
      to: to.toISOString(),
      max_points: String(maxPoints),
    })
    return normalizeSeries(
      await request<unknown>(`/devices/${encodeURIComponent(id)}/metrics?${query}`, token, {
        signal,
      }),
    )
  },

  metricsExportUrl(id: string, from: Date, to: Date, maxPoints = 8_000): string {
    const query = new URLSearchParams({
      from: from.toISOString(),
      to: to.toISOString(),
      max_points: String(maxPoints),
    })
    return `${API_BASE}/devices/${encodeURIComponent(id)}/metrics/export?${query}`
  },

  async availability(
    token: string,
    id: string,
    from: Date,
    to: Date,
    signal?: AbortSignal,
  ): Promise<Availability> {
    const query = new URLSearchParams({
      from: from.toISOString(),
      to: to.toISOString(),
      segments: '90',
    })
    return normalizeAvailability(
      await request<unknown>(`/devices/${encodeURIComponent(id)}/availability?${query}`, token, {
        signal,
      }),
    )
  },

  async analytics(
    token: string,
    id: string,
    from: Date,
    to: Date,
    signal?: AbortSignal,
  ): Promise<Analytics> {
    const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() })
    return normalizeAnalytics(
      await request<unknown>(`/devices/${encodeURIComponent(id)}/analytics?${query}`, token, {
        signal,
      }),
    )
  },

  async uptime(token: string, days = 7): Promise<UptimeSummary> {
    const cached = api.cachedUptime(token, days)
    if (cached) return cached
    const key = `${token}:${days}`
    const existing = uptimeRequests.get(key)
    if (existing) return existing
    const generation = queryCacheGeneration
    const pending = (async () => {
      const query = new URLSearchParams({ days: String(days) })
      const raw = record(await request<unknown>(`/uptime?${query}`, token))
      const result = {
        generatedAt: text(raw.generated_at),
        days: number(raw.days, days),
        from: text(raw.from),
        to: text(raw.to),
        items: array(raw.items).map(normalizeUptimeEntry),
      }
      if (generation === queryCacheGeneration) {
        uptimeCache.set(key, { expires: Date.now() + 30_000, data: result })
      }
      return result
    })().finally(() => {
      if (uptimeRequests.get(key) === pending) uptimeRequests.delete(key)
    })
    uptimeRequests.set(key, pending)
    return pending
  },

  async appearance(token: string): Promise<AppearanceSettings> {
    return normalizeAppearance(await request<unknown>('/settings/appearance', token))
  },

  async updateAppearance(token: string, settings: AppearanceSettings): Promise<AppearanceSettings> {
    return normalizeAppearance(
      await request<unknown>('/settings/appearance', token, {
        method: 'PUT',
        body: JSON.stringify(
          Object.fromEntries(
            Object.entries(settings).map(([key, value]) => [
              key.replace(/[A-Z]/g, (letter) => '_' + letter.toLowerCase()),
              value,
            ]),
          ),
        ),
      }),
    )
  },

  async uploadBackground(token: string, image: string): Promise<string> {
    const result = await request<{ url: string }>('/settings/appearance/background', token, {
      method: 'POST',
      body: JSON.stringify({ image }),
    })
    return result.url
  },

  async backgroundImage(token: string, url: string, signal?: AbortSignal): Promise<Blob> {
    if (!/^\/api\/v1\/settings\/appearance\/background\/[\da-f]{32}\.webp$/.test(url))
      throw new Error('Invalid background path')
    const response = await fetch(url, {
      credentials: 'same-origin',
      signal: signal
        ? AbortSignal.any([signal, AbortSignal.timeout(30_000)])
        : AbortSignal.timeout(30_000),
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new ApiError('Unable to load background image', response.status)
    return response.blob()
  },

  async alertSettings(token: string): Promise<AlertSettings> {
    return normalizeAlerts(await request<unknown>('/settings/thresholds', token))
  },

  async updateAlertSettings(token: string, settings: AlertSettings): Promise<AlertSettings> {
    return normalizeAlerts(
      await request<unknown>('/settings/thresholds', token, {
        method: 'PUT',
        body: JSON.stringify({
          cpu_percent: settings.cpuPercent,
          memory_percent: settings.memoryPercent,
          disk_percent: settings.diskPercent,
          network_bytes_per_sec: settings.networkBytesPerSecond,
          offline_seconds: settings.offlineSeconds,
          cooldown_seconds: settings.cooldownSeconds,
          email_recipients: settings.emailRecipients,
          cpu_enabled: settings.cpuEnabled,
          memory_enabled: settings.memoryEnabled,
          disk_enabled: settings.diskEnabled,
          network_enabled: settings.networkEnabled,
          offline_enabled: settings.offlineEnabled,
          cpu_duration_seconds: settings.cpuDurationSeconds,
          memory_duration_seconds: settings.memoryDurationSeconds,
          disk_duration_seconds: settings.diskDurationSeconds,
          network_duration_seconds: settings.networkDurationSeconds,
          offline_duration_seconds: settings.offlineDurationSeconds,
          cpu_strategy: settings.cpuStrategy,
          memory_strategy: settings.memoryStrategy,
          disk_strategy: settings.diskStrategy,
          network_strategy: settings.networkStrategy,
          offline_strategy: settings.offlineStrategy,
        }),
      }),
    )
  },

  async config(token: string): Promise<PublicConfig> {
    const raw = record(await request<unknown>('/config', token))
    return { smtpEnabled: boolean(record(raw.features).smtp) }
  },

  async adminSmtp(token: string): Promise<AdminSmtpSettings> {
    return normalizeAdminSmtp(await request<unknown>('/admin/smtp', token))
  },

  async updateAdminSmtp(token: string, settings: AdminSmtpUpdate): Promise<AdminSmtpSettings> {
    return normalizeAdminSmtp(
      await request<unknown>('/admin/smtp', token, {
        method: 'PUT',
        body: JSON.stringify(smtpRequest(settings)),
      }),
    )
  },

  async sendAdminSmtpTest(
    token: string,
    settings: AdminSmtpUpdate,
    recipient: string,
  ): Promise<SmtpTestResult> {
    const raw = record(
      await request<unknown>('/admin/smtp/test', token, {
        method: 'POST',
        body: JSON.stringify({ ...smtpRequest(settings), recipient }),
      }),
    )
    return {
      success: boolean(raw.success),
      recipient: text(raw.recipient),
      security: raw.security === 'ssl' || raw.security === 'none' ? raw.security : 'starttls',
    }
  },

  async backupSettings(token: string): Promise<BackupSettings> {
    return normalizeBackupSettings(await request<unknown>('/admin/backups/settings', token))
  },

  async updateBackupSettings(
    token: string,
    settings: BackupSettingsUpdate,
  ): Promise<BackupSettings> {
    return normalizeBackupSettings(
      await request<unknown>('/admin/backups/settings', token, {
        method: 'PUT',
        body: JSON.stringify({
          schedule_enabled: settings.scheduleEnabled,
          interval_days: settings.intervalDays,
          target: settings.target,
          endpoint_url: settings.endpointUrl,
          region: settings.region,
          bucket_name: settings.bucketName,
          access_key: settings.accessKey,
          secret_key: settings.secretKey || null,
          clear_secret_key: settings.clearSecretKey,
        }),
      }),
    )
  },

  async backups(token: string): Promise<BackupRecord[]> {
    const raw = record(await request<unknown>('/admin/backups', token))
    return array(raw.items).map(normalizeBackupRecord)
  },

  async createBackup(token: string): Promise<BackupRecord> {
    return normalizeBackupRecord(
      await request<unknown>('/admin/backups', token, { method: 'POST' }),
    )
  },

  async downloadBackup(token: string, backup: BackupRecord): Promise<void> {
    const response = await fetch(
      `${API_BASE}/admin/backups/${encodeURIComponent(backup.id)}/download`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (!response.ok) {
      throw new ApiError(
        i18n.global.t('errors.requestFailed', { status: response.status }),
        response.status,
      )
    }
    const url = URL.createObjectURL(await response.blob())
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = backup.filename
    anchor.click()
    URL.revokeObjectURL(url)
  },
}

export function webSocketUrl(token: string): string {
  const base = new URL(API_BASE, window.location.origin)
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
  base.pathname = `${base.pathname.replace(/\/$/, '')}/ws`
  if (import.meta.env.VITE_WS_TOKEN_QUERY === 'true') base.searchParams.set('token', token)
  return base.toString()
}
